<template>
    <div
        class="quickpay-payment-card"
        :class="{ 'quickpay-payment-card--selected': isSelected }"
        @click="handleSelect"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
    >
        <div class="quickpay-payment-card__header">
            <CompassRadio
                :modelValue="type"
                :checked="isSelected"
                :showLabel="false"
                :isHovered="isHovered"
                class="quickpay-payment-card__radio"
            />
            <h4 class="quickpay-payment-card__title typographies-heading-h4">
                {{ paymentConfig.title }}
            </h4>
            <div class="quickpay-payment-card__logos">
                <img
                    v-for="(logo, index) in paymentConfig.logos"
                    :key="index"
                    :src="logo.src"
                    :alt="logo.alt"
                    class="quickpay-payment-card__logo"
                />
            </div>
        </div>
        <div
            class="quickpay-payment-card__content-wrapper"
            :class="{ 'quickpay-payment-card__content-wrapper--open': isSelected }"
        >
            <div class="quickpay-payment-card__content">
                <p class="quickpay-payment-card__description typographies-paragraph-small-regular">
                    {{ paymentConfig.description }}
                </p>
                <CompassButton
                    :label="paymentConfig.btnLabel"
                    class="quickpay-payment-card__button"
                    @click.stop="handlePaymentClick"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        type: {
            type: String,
            required: true,
            validator: (value) =>
                ['stripe', 'adyenPayByLink', 'paypal', 'atos', 'gocardless'].includes(value),
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
        data: {
            type: [Object, Boolean],
            required: false,
            default: () => ({}),
        },
    },
    data() {
        return {
            isHovered: false,
        };
    },
    computed: {
        paymentConfig() {
            const configs = {
                stripe: {
                    title: this.$t.__('common.labels.bank_card'),
                    logos: [
                        {
                            src: '/ressources/logged/img/illustrations/payment/cartebleue.svg',
                            alt: 'Carte Bleue',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/visa.svg',
                            alt: 'Visa',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/mastercard.svg',
                            alt: 'Mastercard',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/americanexpress.svg',
                            alt: 'American Express',
                        },
                    ],
                    description: this.$t.__('quickpay.QuickpayPaymentCard.card.description'),
                    btnLabel: this.$t.__('common.actions.bank_card_payment'),
                },
                adyenPayByLink: {
                    title: this.$t.__('common.labels.bank_card'),
                    logos: [
                        {
                            src: '/ressources/logged/img/illustrations/payment/cartebleue.svg',
                            alt: 'Carte Bleue',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/visa.svg',
                            alt: 'Visa',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/mastercard.svg',
                            alt: 'Mastercard',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/americanexpress.svg',
                            alt: 'American Express',
                        },
                    ],
                    description: this.$t.__('quickpay.QuickpayPaymentCard.card.description'),
                    btnLabel: this.$t.__('common.actions.bank_card_payment'),
                },
                atos: {
                    title: this.$t.__('common.labels.bank_card'),
                    logos: [
                        {
                            src: '/ressources/logged/img/illustrations/payment/cartebleue.svg',
                            alt: 'Carte Bleue',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/visa.svg',
                            alt: 'Visa',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/mastercard.svg',
                            alt: 'Mastercard',
                        },
                        {
                            src: '/ressources/logged/img/illustrations/payment/americanexpress.svg',
                            alt: 'American Express',
                        },
                    ],
                    description: this.$t.__('quickpay.QuickpayPaymentCard.card.description'),
                    btnLabel: this.$t.__('common.actions.bank_card_payment'),
                },
                gocardless: {
                    title: this.$t.__('common.labels.sepa_direct_debit'),
                    logos: [
                        {
                            src: '/ressources/logged/img/illustrations/payment/sepa.svg',
                            alt: 'SEPA',
                        },
                    ],
                    description: this.$t.__('quickpay.QuickpayPaymentCard.sepa.description'),
                    btnLabel: this.$t.__('common.actions.continue_to_direct_debit'),
                },
                paypal: {
                    title: 'PayPal',
                    logos: [
                        {
                            src: '/ressources/logged/img/illustrations/payment/paypal.svg',
                            alt: 'PayPal',
                        },
                    ],
                    description: this.$t.__('quickpay.QuickpayPaymentCard.paypal.description'),
                    btnLabel: this.$t.__('common.actions.pay_with_paypal'),
                },
            };

            return configs[this.type] || configs.card;
        },
    },
    methods: {
        handleSelect() {
            this.$emit('select', this.type);
        },
        handlePaymentClick() {
            this.$emit('pay', this.type, this.data);
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-payment-card {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-3);
    background: var(--colors-common-background-default);
    border-radius: var(--spacing-1);
    box-shadow: 0px 0px 9px 0px rgba(143, 143, 143, 0.17);
    transition: all 0.3s ease;
    cursor: pointer;
    &:hover,
    &--selected {
        box-shadow: var(--elevation-1);
    }
    &--selected {
        border: 2px solid var(--colors-primary-default);
    }
    &__header {
        display: flex;
        align-items: center;
    }
    &__radio {
        margin-right: var(--spacing-1);
        pointer-events: none;
    }
    &__title {
        color: var(--colors-common-text-standard-default);
        margin: 0;
        margin-right: var(--spacing-2);
        flex-shrink: 0;
    }
    &__logos {
        display: flex;
        gap: var(--spacing-0);
    }
    &__logo {
        height: var(--spacing-3);
        width: auto;
        object-fit: contain;
    }
    &__content-wrapper {
        display: grid;
        grid-template-rows: 0fr;
        opacity: 0;
        transition: grid-template-rows 0.5s ease, opacity 0.5s ease, margin-top 0.5s ease;
        overflow: hidden;
        &--open {
            grid-template-rows: 1fr;
            opacity: 1;
            margin-top: var(--spacing-1);
        }
    }
    &__content {
        min-height: 0;
    }
    &__description {
        color: var(--colors-common-text-subtle-default);
        margin: 0 0 var(--spacing-3);
    }
    &__button {
        width: 100%;
    }
}
</style>
